import React, { useState } from "react";
import {
  FaDatabase,
  FaDownload,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTerminal,
} from "react-icons/fa";
import apiRequest from "../services/api";

const TenantBackup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [backupResult, setBackupResult] = useState(null);

  // Triggers the backup run (POST with no payload)
  const handleTriggerBackup = async () => {
    setIsLoading(true);
    setError("");
    setBackupResult(null);

    try {
      const resp = await apiRequest("tenants/backup", {
        method: "POST",
      });

      setBackupResult(resp.data || resp);
    } catch (err) {
      setError(
        err?.message ||
          "Backup execution failed. Please verify storage engine write permissions."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-full p-4 pb-24 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 md:p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* PAGE HEADER */}
        <div className="flex flex-col gap-4 p-6 border rounded-2xl backdrop-blur-md bg-white border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-900 dark:text-white">
              <FaDatabase className="text-emerald-600 dark:text-emerald-500" />
              Database Backup
            </h1>

            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Generate an instant snapshot export of your data.
            </p>
          </div>

          <button
            type="button"
            onClick={handleTriggerBackup}
            disabled={isLoading}
            className="flex items-center justify-center gap-2 px-5 py-3 text-xs font-semibold text-white transition-all rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-800 dark:disabled:text-slate-500 shadow-lg shadow-emerald-600/20 disabled:shadow-none"
          >
            {isLoading ? (
              <>
                <FaSpinner className="animate-spin" />
                Compiling Backup...
              </>
            ) : (
              <>
                <FaDownload />
                REQUEST
              </>
            )}
          </button>
        </div>

        {/* ERROR */}
        {error && (
          <div className="flex items-center gap-2 p-4 text-xs font-medium border rounded-xl bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            <FaExclamationTriangle className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* PROCESS / OUTPUT */}
        <div className="grid grid-cols-1 gap-6">
          {/* EMPTY STATE */}
          {!backupResult && !isLoading && !error && (
            <div className="p-8 space-y-3 text-center border rounded-2xl bg-white border-slate-200 shadow-sm dark:bg-slate-900 dark:border-white/10 dark:shadow-2xl">
              <div className="flex items-center justify-center w-12 h-12 mx-auto border rounded-full bg-slate-50 border-slate-200 dark:bg-slate-950/80 dark:border-white/10">
                <FaDatabase className="text-lg text-slate-400 dark:text-slate-500" />
              </div>

              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                No Backup Triggered
              </h3>

              <p className="max-w-md mx-auto text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Click the action button above to request data backup and get
                your backup file through email.
              </p>
            </div>
          )}

          {/* LOADING */}
          {isLoading && (
            <div className="p-12 space-y-4 text-center border rounded-2xl bg-white border-slate-200 shadow-sm dark:bg-slate-900 dark:border-white/10 dark:shadow-2xl">
              <FaSpinner className="mx-auto text-3xl text-emerald-600 animate-spin dark:text-emerald-500" />

              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Processing ...
                </h3>
              </div>
            </div>
          )}

          {/* SUCCESS + RESPONSE */}
          {backupResult && !isLoading && (
            <div className="space-y-4 animate-fadeIn">
              {/* SUCCESS STATUS */}
              <div className="flex items-center gap-2 p-4 text-xs font-medium border rounded-xl bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400">
                <FaCheckCircle className="text-base text-emerald-600 dark:text-emerald-400" />

                <div>
                  <p className="font-semibold">
                    Backup request sent successfully!
                  </p>
                </div>
              </div>

              {/* RESPONSE PAYLOAD */}
              <div className="overflow-hidden border rounded-2xl bg-white border-slate-200 shadow-sm dark:bg-slate-900 dark:border-white/10 dark:shadow-2xl">
                <div className="flex items-center gap-2 px-6 py-4 border-b bg-slate-50 border-slate-200 dark:bg-slate-950/50 dark:border-white/10">
                  <FaTerminal className="text-xs text-slate-400" />

                  <h3 className="text-xs font-bold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                    Request Response
                  </h3>
                </div>

                <div className="max-h-[400px] overflow-x-auto p-6 font-mono text-xs leading-relaxed bg-slate-50/80 text-slate-600 dark:bg-slate-950/80 dark:text-slate-300">
                  <pre className="whitespace-pre-wrap">
                    {JSON.stringify(backupResult.message, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantBackup;
